import React from 'react'
import {
  History,
  Crown,
  MapPin,
  Star,
  Brain,
  Heart,
  Timer,
  CheckCircle2
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'

/**
 * OPTIONAL: If you want more than 4 characteristics,
 * map them dynamically to icons/colors. This array can be extended or customized.
 */
const iconSet = [
  { icon: CheckCircle2, color: 'emerald' },
  { icon: Brain, color: 'blue' },
  { icon: Heart, color: 'rose' },
  { icon: Timer, color: 'purple' }
]

// Helper function to format breed name from snake_case to Title Case
const formatBreedName = (name) => {
  if (!name) return '';
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const DescripcionGeneralCard = ({ descripcionGeneral, razaPrincipal }) => {
  if (!descripcionGeneral) return null

  const { descripcion, origenHistoria, caracteristicasDestacadas = [] } =
    descripcionGeneral

  return (
    <Card className="overflow-hidden bg-white shadow-sm">
      {/* Header */}
      <CardHeader className="border-b bg-gradient-to-br from-blue-500/10 to-transparent p-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md">
            <Crown className="h-6 w-6 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl font-semibold text-gray-800">
              Conoce al {formatBreedName(razaPrincipal)}
            </CardTitle>
            <p className="text-base text-gray-600 mt-1 leading-relaxed">
              {descripcion}
            </p>
          </div>
        </div>
      </CardHeader>

      {/* Content */}
      <CardContent className="p-4 space-y-5">
        {/* ORIGEN E HISTORIA */}
        <div className="bg-gradient-to-br from-amber-50 to-white rounded-lg border border-amber-100 hover:shadow-md transition-shadow">
          <div className="p-4 space-y-4">
            {/* Header: Title + Origin Badge */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <History className="h-5 w-5 text-amber-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  Origen e Historia
                </h3>
              </div>
              <div className="flex items-center gap-2 px-3 py-1 bg-amber-100 rounded-full">
                <MapPin className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-700">
                  {origenHistoria.origen}
                </span>
              </div>
            </div>

            {/* Main Story */}
            <div className="bg-white p-3 rounded-lg border border-amber-50 hover:border-amber-200 transition-colors">
              <p className="text-base text-gray-700 leading-relaxed">
                {origenHistoria.historia}
              </p>
            </div>

            {/* Historical Facts */}
            <div className="grid gap-2">
              {origenHistoria.datosHistoricos.map((dato, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2 bg-amber-50 p-3 rounded-lg hover:bg-amber-100 transition-colors"
                >
                  <Star className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-base text-gray-700 leading-relaxed">
                    {dato}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CARACTERÍSTICAS DESTACADAS */}
        {caracteristicasDestacadas.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-lg font-medium text-gray-800">Características Destacadas</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {caracteristicasDestacadas.map((caracteristica, idx) => {
                // Fallback if we have more characteristics than iconSet
                const { icon: Icon, color } =
                  iconSet[idx % iconSet.length] || iconSet[0]

                return (
                  <div
                    key={idx}
                    className={`bg-${color}-50 p-4 rounded-lg border border-${color}-100 hover:shadow-md transition-shadow flex items-start gap-3`}
                  >
                    <div className="p-2 bg-white rounded-md shadow-sm">
                      <Icon className={`h-5 w-5 text-${color}-500`} />
                    </div>
                    <p className="text-base text-gray-700 leading-relaxed">
                      {caracteristica}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default DescripcionGeneralCard
